/**
 * Motion path preview overlay (W2-11 batch-tools; DESIGN.md §4.14, §5.8; RPE CMOVE-05).
 *
 * While the Motion path sheet is open it publishes the line and time span it writes (`setPathPreview`); the
 * overlay draws where the line's anchor really goes over that span, sampled from the engine (so the live ghost of
 * the tool, parents and every layer are included: preview = export), as a polyline coloured from green at the start
 * to red at the end. Colour is not the only signal: the start is a ring, the end a square, and the line's position
 * at the playhead a filled dot.
 */

import type { LineId } from '../../core/model/types';
import type { Sec } from '../../core/time/types';
import type { Unsubscribe } from '../../core/util/types';
import type { OverlayDrawContext, PreviewOverlayProvider } from '../../render/types';

export interface PathPreview {
  readonly line: LineId;
  readonly t0: Sec;
  readonly t1: Sec;
}

let current: PathPreview | null = null;
const listeners = new Set<() => void>();

/** Publishes (or clears) the span the overlay draws. */
export function setPathPreview(p: PathPreview | null): void {
  if (p === current) return;
  current = p;
  for (const cb of listeners) cb();
}

export function pathPreview(): PathPreview | null {
  return current;
}

export function subscribePathPreview(cb: () => void): Unsubscribe {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Samples along the span. */
export const PATH_OVERLAY_SAMPLES = 160;

type Rgb = readonly [number, number, number];

/** Parses "#rgb", "#rrggbb" or "rgb(r g b)" / "rgb(r, g, b)"; null otherwise. */
export function parseColor(v: string): Rgb | null {
  const s = v.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex !== null) {
    const h = hex[1] ?? '';
    const full =
      h.length === 3 ? `${h.charAt(0)}${h.charAt(0)}${h.charAt(1)}${h.charAt(1)}${h.charAt(2)}${h.charAt(2)}` : h;
    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
  }
  const rgb = /^rgba?\(\s*(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)/i.exec(s);
  if (rgb !== null) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

interface OverlayColors {
  readonly start: string;
  readonly end: string;
  readonly mix: (q: number) => string;
  readonly outline: string;
}

function readToken(el: Element | null, name: string): string {
  if (el === null || typeof getComputedStyle !== 'function') return '';
  try {
    return getComputedStyle(el).getPropertyValue(name).trim();
  } catch {
    return '';
  }
}

/** Colours from the design tokens (success -> danger), with system colours when no stylesheet is loaded. */
export function overlayColors(el: Element | null): OverlayColors {
  const start = readToken(el, '--color-success') || 'Highlight';
  const end = readToken(el, '--color-danger') || 'Mark';
  const outline = readToken(el, '--color-bg') || 'Canvas';
  const a = parseColor(start);
  const b = parseColor(end);
  const mix =
    a !== null && b !== null
      ? (q: number): string =>
          `rgb(${String(Math.round(a[0] + (b[0] - a[0]) * q))} ${String(Math.round(a[1] + (b[1] - a[1]) * q))} ${String(Math.round(a[2] + (b[2] - a[2]) * q))})`
      : (q: number): string => (q < 0.5 ? start : end);
  return { start, end, mix, outline };
}

/** Canvas pixel positions of the line's anchor over the span (nulls where the engine has no state). */
export function samplePath(
  c: OverlayDrawContext,
  p: PathPreview,
  samples = PATH_OVERLAY_SAMPLES,
): ([number, number] | null)[] {
  const out: ([number, number] | null)[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = p.t0 + ((p.t1 - p.t0) * i) / samples;
    try {
      const st = c.engine.lineState(p.line, t);
      const [px, py] = c.worldToPx(st.x, st.y);
      out.push(Number.isFinite(px) && Number.isFinite(py) ? [px, py] : null);
    } catch {
      out.push(null);
    }
  }
  return out;
}

function drawPath(c: OverlayDrawContext, p: PathPreview): void {
  const { ctx, dpr } = c;
  const pts = samplePath(c, p);
  const colors = overlayColors(ctx.canvas instanceof Element ? ctx.canvas : null);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Dark outline under the coloured path keeps it readable over bright lines and backgrounds.
  ctx.lineWidth = 5 * dpr;
  ctx.strokeStyle = colors.outline;
  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  let open = false;
  for (const pt of pts) {
    if (pt === null) {
      open = false;
      continue;
    }
    if (open) ctx.lineTo(pt[0], pt[1]);
    else ctx.moveTo(pt[0], pt[1]);
    open = true;
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 2.5 * dpr;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (a === null || a === undefined || b === null || b === undefined) continue;
    ctx.strokeStyle = colors.mix(i / Math.max(1, pts.length - 2));
    ctx.beginPath();
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
    ctx.stroke();
  }
  const first = pts.find((x) => x !== null);
  const last = [...pts].reverse().find((x) => x !== null);
  if (first !== undefined) {
    ctx.strokeStyle = colors.start;
    ctx.lineWidth = 2 * dpr;
    ctx.beginPath();
    ctx.arc(first[0], first[1], 6 * dpr, 0, Math.PI * 2);
    ctx.stroke();
  }
  if (last !== undefined) {
    ctx.fillStyle = colors.end;
    const h = 5 * dpr;
    ctx.fillRect(last[0] - h, last[1] - h, 2 * h, 2 * h);
  }
  if (c.t >= p.t0 && c.t <= p.t1) {
    try {
      const st = c.engine.lineState(p.line, c.t);
      const [px, py] = c.worldToPx(st.x, st.y);
      ctx.fillStyle = colors.mix((c.t - p.t0) / Math.max(1e-9, p.t1 - p.t0));
      ctx.beginPath();
      ctx.arc(px, py, 4 * dpr, 0, Math.PI * 2);
      ctx.fill();
    } catch {
      // The line may have been removed while the sheet was open.
    }
  }
  ctx.restore();
}

/** The overlay provider (FeatureModule.previewOverlays). */
export function pathOverlayProvider(): PreviewOverlayProvider {
  return {
    id: 'batch.motionPath',
    order: 60,
    draw(c) {
      const p = current;
      if (p === null || !(p.t1 > p.t0)) return;
      drawPath(c, p);
    },
  };
}
